import { useRef, useEffect, useCallback } from 'react';
import { CameraControls } from '@react-three/drei';
import { Subject, SubjectViewState } from './Subject';
import { Context } from './Context';
import { SubjectConfig, AreaConfig, LabelConfig } from '#server/models';
import { SearchLabelState } from '@/components';
import { NavigationControlsEventListener } from '@/components/Common/EventsHandler';

interface ExperienceProps {
  subjectModelPath: string;
  subjectTexturePath: string;
  referenceModelPath: string;
  subjectContextModelPath: string;
  modelConfig: SubjectConfig;
  areaConfig: AreaConfig;
  setSideMenuState: any;
  subjectViewState: SubjectViewState;
  setSubjectViewState: (subjectViewState: SubjectViewState) => void;
  searchLabelsState: SearchLabelState;
  getUpdatedLabels: () => LabelConfig[] | undefined;
  toggleResetView: () => void;
  handleSubjectLoadProgress: (progress: number) => void;
}
export const Experience = ({
  subjectModelPath,
  subjectTexturePath,
  referenceModelPath,
  subjectContextModelPath,
  modelConfig,
  areaConfig,
  setSideMenuState,
  subjectViewState,
  setSubjectViewState,
  searchLabelsState,
  getUpdatedLabels,
  toggleResetView,
  handleSubjectLoadProgress
}: ExperienceProps) => {
  const modelSettings = modelConfig.config3D!;
  const cameraControlsRef = useRef<CameraControls | null>(null);
  const { reset } = subjectViewState;

  // Use a callback ref so mouse/touch button config is applied as soon as
  // CameraControls mounts. Using ref.current as a useEffect dependency is
  // invalid in React — refs aren't reactive — and breaks under React 19 /
  // R3F v9 where the timing guarantee that made it work no longer holds.
  const cameraControlsCallback = useCallback((controls: CameraControls | null) => {
    cameraControlsRef.current = controls;
    if (controls) {
      // camera-controls ACTION enum (constants not exposed by DREI).
      // These values are from the installed version of camera-controls which added
      // SCREEN_PAN between TRUCK and OFFSET, shifting the values relative to
      // the original commit referenced below.
      // https://github.com/yomotsu/camera-controls/blob/ce352346583bc4ad4666fcff32b74e84bc7bc513/src/types.ts#L33
      // OFFSET            = 0b1000        =   8
      // TOUCH_OFFSET      = 0b1000000000  = 512
      // TOUCH_DOLLY_OFFSET= 0b100000000000000 = 16384
      controls.mouseButtons.right = 8;
      controls.touches.two = 16384;
      controls.touches.three = 512;
    }
  }, []);

  useEffect(() => {
    if (reset) {
      cameraControlsRef?.current?.reset(true);
      cameraControlsRef?.current?.dollyTo(modelSettings.dist, true);
      setSubjectViewState({ ...subjectViewState, reset: false });
    }
  }, [reset]);

  return (
    <>
      <CameraControls
        makeDefault
        ref={cameraControlsCallback}
        draggingSmoothTime={0.3}
        distance={modelSettings.dist}
        minDistance={modelSettings.distMin}
        maxDistance={modelSettings.distMax}
      />
      <NavigationControlsEventListener
        resetViewHandler={toggleResetView}
        cameraControlsRef={cameraControlsRef}
      />
      <Subject
        modelPath={subjectModelPath}
        texturePath={subjectTexturePath}
        modelConfig={modelConfig}
        areaConfig={areaConfig}
        cameraControlsRef={cameraControlsRef}
        setSideMenuState={setSideMenuState}
        subjectViewState={subjectViewState}
        setSubjectViewState={setSubjectViewState}
        searchLabelsState={searchLabelsState}
        getUpdatedLabels={getUpdatedLabels}
        handleSubjectLoadProgress={handleSubjectLoadProgress}
      />
      <Context
        referenceModelPath={referenceModelPath}
        subjectContextModelPath={subjectContextModelPath}
        contextConfig={modelSettings.context}
        cameraControlRef={cameraControlsRef}
        subjectViewState={subjectViewState}
      />
    </>
  );
};
